import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import warehouseService from '../../services/warehouseService';
import './WarehouseDetailPage.css';
import { ArrowLeft, PenLine, Trash2 } from 'lucide-react';

const WarehouseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [warehouse, setWarehouse] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadWarehouse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadWarehouse = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await warehouseService.getWarehouseById(parseInt(id || '0'));
      setWarehouse(response?.data?.data || response?.data);
      setLoading(false);
    } catch (err) {
      setError(t('warehouse.failedToLoadWarehouse'));
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!warehouse) return;
    navigate(`/organization/warehouses/${warehouse.id}/delete`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!warehouse) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{t('warehouse.failedToLoadWarehouse')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="detail-container">
        {/* Navigation Breadcrumb */}
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/organization/warehouses')}
            title={t('warehouse.backToWarehouses')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('warehouse.title')}
          </button>
        </nav>

        {/* Page Title */}
        <div className="detail-header">
          <h1 className="detail-title">{warehouse.name}</h1>
          <p className="detail-subtitle">{t('warehouse.viewTitle')}</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="detail-content">
          {/* Details Section */}
          <div className="detail-main-section">
            {/* Warehouse Details Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('warehouse.details')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('warehouse.warehouseName')}</label>
                    <p className="detail-value">{warehouse.name}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('warehouse.code')}</label>
                    <p className="detail-value detail-code">{warehouse.code}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('warehouse.location')}</label>
                    <p className="detail-value">{warehouse.location}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('warehouse.address')}</label>
                    <p className="detail-value">
                      {warehouse.address || <em className="detail-empty">{t('warehouse.noAddressProvided')}</em>}
                    </p>
                  </div>

                  {warehouse.city && (
                    <div className="detail-field">
                      <label className="detail-label">{t('warehouse.city')}</label>
                      <p className="detail-value">{warehouse.city}</p>
                    </div>
                  )}

                  {warehouse.state && (
                    <div className="detail-field">
                      <label className="detail-label">{t('warehouse.state')}</label>
                      <p className="detail-value">{warehouse.state}</p>
                    </div>
                  )}

                  {warehouse.postalCode && (
                    <div className="detail-field">
                      <label className="detail-label">{t('warehouse.postalCode')}</label>
                      <p className="detail-value">{warehouse.postalCode}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Information Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('common.info')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">ID</label>
                    <p className="detail-value detail-code">{warehouse.id}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.status')}</label>
                    <div className="detail-value">
                      <span className="info-badge info-badge-active">
                        {warehouse.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdDate')}</label>
                    <p className="detail-value">
                      {new Date(warehouse.createdAt).toLocaleDateString(
                        i18n.language === 'ja' ? 'ja-JP' : 'en-US'
                      )}
                    </p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdTime')}</label>
                    <p className="detail-value">
                      {new Date(warehouse.createdAt).toLocaleTimeString(
                        i18n.language === 'ja' ? 'ja-JP' : 'en-US'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="detail-sidebar">
            {/* Actions Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body detail-actions">
                <button
                  className="btn-save"
                  onClick={() => navigate(`/organization/warehouses/${warehouse.id}/edit`)}
                  title={t('warehouse.editWarehouse')}
                >
                  <PenLine size={16} strokeWidth={2} />
                  {t('common.edit')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  title={t('warehouse.deleteWarehouse')}
                >
                  <Trash2 size={16} strokeWidth={2} />
                  {t('common.delete')}
                </button>

                <div className="detail-divider"></div>

                <button
                  className="detail-btn detail-btn-secondary"
                  onClick={() => navigate('/organization/warehouses')}
                  title={t('warehouse.backToWarehouses')}
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                  {t('warehouse.backToWarehouses')}
                </button>
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.quickActions')}</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/organization/warehouses/${warehouse.id}/edit`)}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('common.edit')}</span>
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/organization/warehouses')}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('warehouse.backToWarehouses')}</span>
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/organization/warehouses/create')}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('warehouse.newWarehouse')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WarehouseDetailPage;
